import React, { useEffect, useReducer, useRef } from 'react';
import axios from 'axios';
import { defineMessages } from 'react-intl.macro';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { get, head, find, replace } from 'lodash';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core';

import { selectCurrentMasterSettings } from '@lib/selectors';
import useGeolocation from '@lib/hooks/useGeolocation';
import configService from '@lib/services/configService';

import { TextField, Typography, MaskedInputZipcode, TDistance } from 'components/v2';

const messages = defineMessages({
  text1: {
    id: 'core.Filters.FilterDistance.text1',
    defaultMessage: 'Within',
  },
  text2: {
    id: 'core.Filters.FilterDistance.text2',
    defaultMessage: 'mi of',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      padding: `0px ${unit * 2}px`,
      lineHeight: '30px',
    },
    input: {
      width: 70,
      margin: `0px ${unit}px`,
    },
  });

interface FilterDistanceProps extends WithStyles<typeof styles> {
  onFilterChange: (value: any) => void;
  value: string | string[] | TDistance | null;
}

const FilterDistance: React.FC<FilterDistanceProps> = ({ classes, onFilterChange, value }) => {
  const { formatMessage } = useIntl();
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const [latitude, longitude] = useGeolocation();
  const shouldUseCurrentLocation = useRef(true);

  const [filter, setFilter] = useReducer((state: TDistance, newState: TDistance) => ({ ...state, ...newState }), {
    zipcode: get(value, 'zipcode', ''),
    zipcodeMaxDistance: get(value, 'zipcodeMaxDistance', get(currentMasterSettings, 'scheduler.zipcodeMaxDistance', 2)),
  });

  const handleInputChange = (_value: TDistance) => {
    setFilter(_value);

    if (_value.zipcode.length === 5 || _value.zipcode.length === 0) {
      onFilterChange(_value);
    }
  };

  useEffect(() => {
    if (latitude && longitude && get(value, 'zipcode', '') === '' && shouldUseCurrentLocation.current) {
      (async () => {
        // TODO should create a hook and maybe use react-query
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${configService.getKeyGooglMaps()}`,
        );
        if (response) {
          // first result is the nearest location based on user location
          const address = get(head(get(response, 'data.results')), 'address_components');
          const zipcode = replace(
            get(find(address as any, { types: ['postal_code'] }), 'long_name', ''),
            /[^\d]/g,
            '',
          ).slice(0, 5);

          shouldUseCurrentLocation.current = false;

          // update filters with closest zipcode based on user location
          setFilter({ ...filter, zipcode });

          // fires a search with updated filters
          onFilterChange({ ...filter, zipcode });
        }
      })();
    }
  }, [filter, latitude, longitude, onFilterChange, setFilter, value]);

  return (
    <div className={classes.root}>
      <Typography color="primary" variant="bW500F17" inline>
        {formatMessage(messages.text1)}
      </Typography>
      <TextField
        InputProps={{
          className: classes.input,
          onChange: (event) => handleInputChange({ ...filter, zipcodeMaxDistance: parseInt(event.target.value, 10) }),
          inputProps: {
            min: 0,
            max: 9999,
          },
        }}
        value={filter.zipcodeMaxDistance}
        label=""
        name="zipcodeMaxDistance"
        type="number"
      />
      <Typography color="primary" variant="bW500F17" inline>
        {formatMessage(messages.text2)}
      </Typography>
      <TextField
        InputProps={{
          className: classes.input,
          inputComponent: MaskedInputZipcode,
          onChange: (event) => handleInputChange({ ...filter, zipcode: event.target.value.replace(/[^\d]/g, '') }),
          placeholder: 'zipcode',
        }}
        value={filter.zipcode}
        label=""
        name="zipcode"
      />
    </div>
  );
};

export default withStyles(styles)(FilterDistance);
